import { IService, ServiceEvent } from '../base/BaseService';
import type { TextElement, PunctElement } from '../../models/TranscriptElement';
import type { Segment } from '../../models/Segment';

/**
 * Event types for the SelectionService
 */
export enum SelectionEvent {
  SELECTION_CHANGED = 'selection_changed',
  ELEMENT_SELECTED = 'element_selected',
  SELECTION_CLEARED = 'selection_cleared'
}

/**
 * Make SelectionEvent compatible with ServiceEvent
 */
// Enum extends implicitly

/**
 * Represents a selected element in the transcript
 */
export interface SelectedElement {
  id: string;
  segmentId: string;
  paragraphId: string;
  element: TextElement | PunctElement;
  type: 'text' | 'punct';
}

/**
 * Interface for observing selection events
 */
export interface SelectionObserver {
  onSelectionEvent(eventType: SelectionEvent, payload?: any): void;
}

/**
 * Interface for the selection service
 */
export interface ISelectionService extends IService<SelectionEvent> {
  /**
   * Selects a single element
   */
  selectElement(
    element: TextElement | PunctElement,
    segmentId: string,
    paragraphId: string,
    clearExisting?: boolean
  ): void;
  
  /**
   * Selects multiple elements
   */
  selectElements(
    elements: Array<{
      element: TextElement | PunctElement;
      segmentId: string;
      paragraphId: string;
    }>,
    clearExisting?: boolean
  ): void;
  
  /**
   * Sets the selected segment
   */
  setSelectedSegment(segment: Segment): void;
  
  /**
   * Deselects a specific element
   */
  deselectElement(elementId: string): void;
  
  /**
   * Clears all selected elements
   */
  clearSelectedElements(): void;
  
  /**
   * Clears all selections (both elements and segment)
   */
  clearAllSelections(): void;
  
  /**
   * Checks if a specific element is currently selected
   */
  isElementSelected(elementId: string): boolean;
  
  /**
   * Gets the currently selected elements
   */
  getSelectedElements(): SelectedElement[];
  
  /**
   * Legacy method to register an observer (for backwards compatibility)
   */
  registerObserver(observer: SelectionObserver): void;
  
  /**
   * Legacy method to unregister an observer (for backwards compatibility)
   */
  unregisterObserver(observer: SelectionObserver): void;
  
  /**
   * Register for selection updates with a callback
   */
  registerForSelectionUpdates(callback: (selectedElements: SelectedElement[]) => void): () => void;
  
  /**
   * Sets the selected elements directly
   */
  setSelectedElements(elements: SelectedElement[]): void;
  
  /**
   * Array of currently selected elements
   */
  readonly selectedElements: SelectedElement[];
  
  /**
   * Currently selected segment
   */
  readonly selectedSegment: Segment | null;
  
  /**
   * Current selection type
   */
  readonly currentSelectionType: 'elements' | 'segment' | null;
  
  /**
   * Whether there is any selection
   */
  readonly hasSelection: boolean;
}